import React, { useEffect } from 'react';
import { X, Sparkles, CornerDownLeft } from 'lucide-react';
import type { SamplePrompt } from './types';

/**
 * Prompt-templates POPUP — a format reference the user opens any time
 * while interacting. Shows each template's FULL text (the exact
 * format, with ALL-CAPS placeholders) so the user can see how to
 * phrase it. "Use this" drops it into the chat input (prefill —
 * never auto-send). Shared by every agent.
 */
interface Props {
  open: boolean;
  onClose: () => void;
  prompts: SamplePrompt[];
  onPick: (prompt: string) => void;
}

export const PromptTemplatesModal: React.FC<Props> = ({ open, onClose, prompts, onPick }) => {
  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[80vh] flex flex-col bg-cargo-panel border border-cargo-border rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-cargo-border">
          <div>
            <h3 className="text-cargo-text font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-cargo-accent" />
              Prompt templates · format reference
            </h3>
            <p className="text-cargo-muted text-xs mt-1">
              These are <span className="text-cargo-text">examples</span>. Click{' '}
              <span className="text-cargo-text">Use this</span> to drop one into the chat, then
              change the values (account, database, table, password) to your own.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-hover/30 transition-colors flex-shrink-0"
            title="Close (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Templates */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {prompts.map((p, i) => (
            <div key={i} className="rounded-lg border border-cargo-border bg-cargo-surface">
              <div className="px-3 pt-2.5 text-cargo-text text-[13px] font-medium">{p.label}</div>
              {p.help && (
                <p className="px-3 pt-1 text-[12px] leading-5 text-cargo-muted">
                  <span className="text-cargo-text font-medium">What it does — </span>
                  {p.help}
                </p>
              )}
              <pre className="px-3 py-2 text-[12px] leading-5 text-cargo-muted font-mono whitespace-pre-wrap break-words">
                {p.prompt}
              </pre>
              <div className="flex justify-end px-3 pb-2.5">
                <button
                  onClick={() => { onPick(p.prompt); onClose(); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cargo-accent text-white text-xs hover:bg-cargo-accent/90 transition-colors"
                >
                  <CornerDownLeft className="w-3.5 h-3.5" />
                  Use this
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PromptTemplatesModal;
